import React from 'react';
import { FormGroup } from '@/designSystem/Form';
import TextFieldStatus from '@/designSystem/TextFieldStatus';
import TextField from '@/designSystem/TextField';
import componentsId from '@/utils/componentsId';
import { contactabilityFormTexts } from '@/utils/generalTexts';
import { regExpRut, regExpSpecialCharacters } from '@/utils/constants';

import { Text, TextFieldTelephone } from '@BICE_Vida/components';

import styles from './ContactabilityForm.module.scss';

interface ContactabilityFormProps {
  readonly companyRut: string;
  readonly companyName: string;
  readonly workersNumber: string;
  readonly contactName: string;
  readonly email: string;
  readonly phone: string;
  readonly formErrors: Record<string, { hasError: boolean; feedback: string }>;
  readonly loading: boolean;
  readonly handleChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  readonly handleBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
  readonly handleKeyPress: (event: React.KeyboardEvent<HTMLInputElement>, regEx: RegExp) => void;
  readonly formatRutOnBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
  readonly formSubmitted: boolean;
}

function ContactabilityForm({
  companyRut,
  companyName,
  workersNumber,
  contactName,
  email,
  phone,
  formErrors,
  loading,
  handleChange,
  handleBlur,
  handleKeyPress,
  formatRutOnBlur,
  formSubmitted,
}: ContactabilityFormProps) {
  return (
    <div className={styles.contact_form_container}>
      <Text tag="h2" color="primary" className={styles.contact_form_section_title}>
        {contactabilityFormTexts.companyMenu}
      </Text>
      <div className={styles.contact_form_section}>
        <FormGroup className={styles.contact_form_field}>
          <TextFieldStatus
            id={componentsId.contactabilityForm.inputCompanyRut}
            label={contactabilityFormTexts.inputCompanyRut}
            value={companyRut || ''}
            onChange={(event: any) => {
              formatRutOnBlur(event);
              handleChange(event);
            }}
            onKeyPress={(event) => handleKeyPress(event, regExpRut)}
            onBlur={handleBlur}
            hasError={formErrors[componentsId.contactabilityForm.inputCompanyRut]?.hasError}
            feedback={formErrors[componentsId.contactabilityForm.inputCompanyRut]?.feedback}
            loading={loading}
            disabled={formSubmitted}
            {...{
              name: 'rut',
              autoComplete: 'off',
            }}
          />
        </FormGroup>
        <FormGroup className={styles.contact_form_field}>
          <TextField
            id={componentsId.contactabilityForm.inputCompanyName}
            label={contactabilityFormTexts.inputCompanyName}
            value={companyName || ''}
            onChange={handleChange}
            disabled={formSubmitted}
            onBlur={handleBlur}
            hasError={formErrors[componentsId.contactabilityForm.inputCompanyName]?.hasError}
            feedback={formErrors[componentsId.contactabilityForm.inputCompanyName]?.feedback}
            onKeyPress={(event: any) => handleKeyPress(event, regExpSpecialCharacters)}
          />
        </FormGroup>
        <FormGroup className={styles.contact_form_field}>
          <TextField
            id={componentsId.contactabilityForm.inputWorkersNumber}
            label={contactabilityFormTexts.inputWorkersNumber}
            value={workersNumber || ''}
            onChange={handleChange}
            disabled={formSubmitted}
            onBlur={handleBlur}
            hasError={formErrors[componentsId.contactabilityForm.inputWorkersNumber]?.hasError}
            feedback={formErrors[componentsId.contactabilityForm.inputWorkersNumber]?.feedback}
            onKeyPress={(event) => handleKeyPress(event, regExpSpecialCharacters)}
          />
        </FormGroup>
      </div>
      <Text tag="h2" color="primary" className={styles.contact_form_section_title}>
        {contactabilityFormTexts.contactMenu}
      </Text>
      <div className={styles.contact_form_section}>
        <FormGroup className={styles.contact_form_field}>
          <TextField
            id={componentsId.contactabilityForm.inputContactName}
            label={contactabilityFormTexts.inputContactName}
            value={contactName || ''}
            onChange={handleChange}
            disabled={formSubmitted}
            onBlur={handleBlur}
            hasError={formErrors[componentsId.contactabilityForm.inputContactName]?.hasError}
            feedback={formErrors[componentsId.contactabilityForm.inputContactName]?.feedback}
            onKeyPress={(event) => handleKeyPress(event, regExpSpecialCharacters)}
          />
        </FormGroup>
        <FormGroup className={styles.contact_form_field}>
          <TextField
            id={componentsId.contactabilityForm.inputEmail}
            label={contactabilityFormTexts.inputEmail}
            value={email || ''}
            onChange={handleChange}
            disabled={formSubmitted}
            onBlur={handleBlur}
            hasError={formErrors[componentsId.contactabilityForm.inputEmail]?.hasError}
            feedback={formErrors[componentsId.contactabilityForm.inputEmail]?.feedback}
            onKeyPress={(event) => handleKeyPress(event, regExpSpecialCharacters)}
          />
        </FormGroup>
        <FormGroup className={styles.contact_form_field}>
          <TextFieldTelephone
            id={componentsId.contactabilityForm.inputPhone}
            label={contactabilityFormTexts.inputPhone}
            value={phone || ''}
            onChange={handleChange}
            disabled={formSubmitted}
            onBlur={handleBlur}
            hasError={formErrors[componentsId.contactabilityForm.inputPhone]?.hasError}
            feedback={formErrors[componentsId.contactabilityForm.inputPhone]?.feedback}
            type="tel"
          />
        </FormGroup>
      </div>
    </div>
  );
}

export default ContactabilityForm;
